/**
 * F7 „Anfos je Kanal und Monat", interaktiv (Deck 23.09., Folie 5): Kanalgruppen an/ab, Kennzahl Anfos |
 * Erwarteter Wert | Bisher bezahlt (Till 20.10.2026, Q4), Ansicht gestapelt | Linien | Anteil %.
 * Darüber die Vergleichszeile des letzten vollen Quartals (vorschlag_v3 §2.3) — gegen Vorjahresquartal UND Vorquartal,
 * die gewählte Basis (E6) steht vorn; die Rechenregel liefert das Backend (`verlauf.vergleich`).
 * Anteil % rechnet IMMER gegen alle Anfos des Monats, nicht gegen die Auswahl — im Deck änderte das Abwählen
 * still jeden Anteil (UX-Prüfung 20.10.). Farben folgen der Gruppe (`vizStyle.kanalFarben`), nie dem Rang.
 * Backend: /api/auswertungen/kanaele/verlauf (24 Monate rollierend, unabhängig vom Kapitel-Zeitraum).
 * Workshop-Lücke E2 (Plan 2026-10-29): gestrichelte Vorjahreslinie (Rechnung in `vorjahr.ts`) — gestapelt/Anteil grau als
 * Σ der Auswahl über den Balken, in der Linienansicht je Gruppe in ihrer Farbe (eine Summenlinie neben Einzelgruppen
 * stauchte die Achse); bei „Bisher bezahlt" keine Linie.
 */
import { CSSProperties, FC, useMemo, useState } from 'react';
import { Box, Chip, Collapse, Link, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarCustomLayerProps, BarDatum, ResponsiveBar } from '@nivo/bar';
import { CustomLayerProps, ResponsiveLine } from '@nivo/line';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { TipRow, achse, unsicherFuellung, unsicherKonturen, useVizStyle } from '../../../components/auswertungen/vizStyle';
import InfoTip from '../../../components/InfoTip';
import { formatEur, formatPct, quartalLang, TERMS } from '../../../helpers/kpiDefinitions';
import {
  fetchKanalVerlauf,
  formatMonat,
  GrauSchwellen,
  KanalGruppeKey,
  Veraenderung,
  VergleichsBasis,
  VerlaufMonat,
  VerlaufVergleich,
} from '../../../helpers/auswertungenData';
import { Hinweis, int, Laden, useLaden } from '../webinare-produkte/shared';
import { formatDelta, tEuro, veraenderungTip } from './shared';
import { vorjahrJeMonat } from './vorjahr';

/** Strichmuster der Vorjahreslinie (E2), überall gleich. */
const VORJAHR_STRICH = '5 4';

type Kennzahl = 'anfos' | 'wert' | 'bezahlt';
type Ansicht = 'stapel' | 'linien' | 'anteil';

const KENNZAHL: Record<Kennzahl, string> = { anfos: TERMS.anfos.label, wert: TERMS.wertBeiBestellung.short, bezahlt: TERMS.umsatzNetto.label };

/** Ein Vergleich der Quartalszeile: „−4 % zum Vorjahresquartal", Zerlegung im Tooltip, grau unter der Schwelle. */
const QuartalsVergleich: FC<{ v: Veraenderung; text: string; schwelle: number }> = ({ v, text, schwelle }) => (
  <Box component="span" sx={{ whiteSpace: 'nowrap', color: v.grau_grund ? 'text.disabled' : 'inherit' }}>
    <Box component="span" sx={{ fontWeight: 700 }}>
      {v.neu ? 'neu' : formatDelta(v.delta)}
    </Box>{' '}
    {text}
    <InfoTip text={veraenderungTip(v, schwelle)} />
  </Box>
);

/**
 * „Q3 2026 (letztes volles Quartal): Erwarteter Wert 120,2 T€ (−10 % zum Vorquartal, −4 % zum Vorjahresquartal) ·
 * 715 Anfos" — die Prozente an der Geldgröße, nicht hinter den Anfos (vorschlag_v3 §2.3).
 */
const Vergleichszeile: FC<{ v: VerlaufVergleich; basis: VergleichsBasis; schwellen: GrauSchwellen }> = ({ v, basis, schwellen }) => {
  const vorjahr = (
    <QuartalsVergleich key="vorjahr" v={v.vorjahr} text={`zum Vorjahresquartal (${quartalLang(v.vorjahr_quartal.label)})`} schwelle={schwellen.veraenderung_anfos} />
  );
  const vorher = (
    <QuartalsVergleich key="vorher" v={v.vorher} text={`zum Vorquartal (${quartalLang(v.vorher_quartal.label)})`} schwelle={schwellen.veraenderung_anfos} />
  );
  const [erst, dann] = basis === 'vorjahr' ? [vorjahr, vorher] : [vorher, vorjahr];
  const vorlaeufig = v.jetzt.anfos_gesamt - v.jetzt.anfos;
  return (
    <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
      <Box component="span" sx={{ fontWeight: 700 }}>
        {quartalLang(v.quartal.label)}
      </Box>{' '}
      (letztes volles Quartal): {TERMS.wertBeiBestellung.short}{' '}
      <Box component="span" sx={{ fontWeight: 700 }}>
        {tEuro(v.jetzt.value_sum)}
      </Box>{' '}
      ({erst}, {dann}) · {int(v.jetzt.anfos_gesamt)} Anfos
      {vorlaeufig > 0 && (
        <InfoTip text={`Davon ${int(vorlaeufig)} vorläufig ergänzt — sie stehen in den Balken, zählen aber nicht in die Veränderung.`} />
      )}
    </Typography>
  );
};

interface MonatsWerte extends VerlaufMonat {
  /** Wert je Gruppe in der gewählten Kennzahl. */
  je: Record<string, number>;
  /** Summe ALLER Gruppen (Nenner des Anteils, unabhängig von der Auswahl). */
  alle: number;
  /** Zahler je Gruppe und gesamt — „Bisher bezahlt" nur als Bündel mit ihnen (Sektionsregel, B77). */
  zahler: Record<string, number>;
  zahlerAlle: number;
  /** Blass: laufender Monat (Anfos, Wert) bzw. junge Monate (bezahlt). */
  blass: boolean;
}

/** Gestrichelter Pfad mit Punkten (E2); `null` unterbricht die Linie (kein Vorjahresmonat). */
const VorjahrPfad: FC<{ punkte: ({ x: number; y: number } | null)[]; farbe: string; grund: string; duenn?: boolean }> = ({ punkte, farbe, grund, duenn }) => {
  const stuecke: { x: number; y: number }[][] = [];
  let laufend: { x: number; y: number }[] = [];
  for (const p of punkte) {
    if (p) laufend.push(p);
    else if (laufend.length) {
      stuecke.push(laufend);
      laufend = [];
    }
  }
  if (laufend.length) stuecke.push(laufend);
  return (
    <g pointerEvents="none">
      {stuecke.map((st, i) => (
        <path
          key={i}
          d={st.map((p, j) => `${j === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke={farbe}
          strokeWidth={duenn ? 1.5 : 2}
          strokeDasharray={VORJAHR_STRICH}
          opacity={duenn ? 0.8 : 1}
        />
      ))}
      {stuecke.flat().map((p, i) => (
        <circle key={`p${i}`} cx={p.x} cy={p.y} r={duenn ? 2.5 : 3} fill={grund} stroke={farbe} strokeWidth={1.5} />
      ))}
    </g>
  );
};

const VerlaufKarte: FC<{ basis: VergleichsBasis }> = ({ basis }) => {
  const { data, fehler } = useLaden(fetchKanalVerlauf);
  const viz = useVizStyle();
  const theme = useTheme();
  const breit = useMediaQuery(theme.breakpoints.up('md'));
  const [kennzahl, setKennzahl] = useState<Kennzahl>('anfos');
  const [ansicht, setAnsicht] = useState<Ansicht>('stapel');
  const [spanne, setSpanne] = useState<12 | 24>(12);
  const [aus, setAus] = useState<Set<KanalGruppeKey>>(new Set());
  // Offen (Till 23.10.: Dashboard-Karten nicht zugeklappt); wer nur die Grafik will, blendet die Zahlen aus.
  const [tabelle, setTabelle] = useState(true);

  const gruppen = data?.groups ?? [];
  const gruppeVon = useMemo(() => new Map((data?.channels ?? []).map((c) => [c.key, c.group])), [data]);
  const sichtbar = gruppen.filter((g) => !aus.has(g.key));

  const monate: MonatsWerte[] = useMemo(() => {
    if (!data) return [];
    const wert = (r: (typeof data.rows)[number]) => (kennzahl === 'anfos' ? r.anfos : kennzahl === 'wert' ? r.value_sum : r.paid_revenue);
    return data.months.slice(-spanne).map((m) => {
      const je: Record<string, number> = {};
      const zahler: Record<string, number> = {};
      for (const g of data.groups) {
        je[g.key] = 0;
        zahler[g.key] = 0;
      }
      for (const r of data.rows) {
        if (r.month !== m.month) continue;
        const g = gruppeVon.get(r.channel) ?? 'other';
        je[g] += wert(r);
        zahler[g] += r.payers;
      }
      const alle = Object.values(je).reduce((s, v) => s + v, 0);
      const zahlerAlle = Object.values(zahler).reduce((s, v) => s + v, 0);
      return { ...m, je, alle, zahler, zahlerAlle, blass: kennzahl === 'bezahlt' ? m.is_young : m.is_partial };
    });
  }, [data, kennzahl, spanne, gruppeVon]);

  // E2: Vorjahr je Monat der Ansicht (Σ der Auswahl bzw. je Gruppe); leer bei „Bisher bezahlt".
  const auswahlKeys = useMemo(() => (data?.groups ?? []).filter((g) => !aus.has(g.key)).map((g) => g.key as string), [data, aus]);
  const vj = useMemo(
    () =>
      data
        ? vorjahrJeMonat(
            { months: monate, rows: data.rows, teilmonat: data.vorjahr_teilmonat, gruppeVon, auswahl: auswahlKeys, kennzahl, anteil: ansicht === 'anteil' },
            data.months.map((m) => m.month),
          )
        : new Map(),
    [data, monate, gruppeVon, auswahlKeys, kennzahl, ansicht],
  );
  // „Anteil %" mit ALLEN Gruppen: das Vorjahr wäre jeden Monat 100 % (eine flache Linie am oberen Rand) — dann keine
  // Linie, sondern ein Satz in der Legende (Abnahme 29.10.). Ohne gewählte Gruppe gibt es nichts zu vergleichen.
  const anteilAlle = ansicht === 'anteil' && auswahlKeys.length === gruppen.length;
  const mitVorjahr = kennzahl !== 'bezahlt' && auswahlKeys.length > 0 && !anteilAlle && vj.size > 0;
  const vjText = (month: string): string => {
    const p = vj.get(month);
    if (!p || p.auswahl === null) return '';
    return `${formatMonat(p.month)}${p.bis ? ` bis ${p.bis.slice(8, 10)}.${p.bis.slice(5, 7)}.` : ''}`;
  };
  const zeig = (v: number, alle: number): number => (ansicht === 'anteil' ? (alle > 0 ? (v / alle) * 100 : 0) : v);
  const fmt = (v: number | null): string =>
    ansicht === 'anteil' ? formatPct(v) : kennzahl === 'anfos' ? int(v) : formatEur(v, 0);
  /** „Bisher bezahlt" nur mit der Zahl der Zahler (Bündel, B77): wenige Sofortzahler lassen junge Monate sonst stark aussehen. */
  const mitZahlern = kennzahl === 'bezahlt' && ansicht !== 'anteil';
  const zahlerText = (n: number) => `${int(n)} Zahler`;
  const zelleBezahlt = (wert: string, n: number) =>
    mitZahlern ? (
      <Box sx={{ lineHeight: 1.25 }}>
        {wert}
        <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', whiteSpace: 'normal' }}>
          {zahlerText(n)}
        </Box>
      </Box>
    ) : (
      wert
    );

  const chartData = monate.map((m) => {
    const row: Record<string, number | string | boolean> = { month: m.month, blass: m.blass, alle: m.alle, zahlerAlle: m.zahlerAlle };
    for (const g of sichtbar) {
      row[g.key] = zeig(m.je[g.key], m.alle);
      row[`${g.key}__zahler`] = m.zahler[g.key] ?? 0;
    }
    return row;
  });
  // Monatsnamen überlappen schon bei 1.280 px ab 24 Werten — vom jüngsten Monat aus jeden n-ten zeigen.
  const schritt = breit ? (spanne === 24 ? 2 : 1) : spanne === 24 ? 6 : 3;
  const ticks = monate.map((m) => m.month).filter((_m, i, a) => (a.length - 1 - i) % schritt === 0);
  const einheit = ansicht === 'anteil' ? '%' : kennzahl === 'anfos' ? '' : '€';
  // Laufender Monat auf der Achse mit Stichtag („Okt 26 (bis 20.)") — blass allein las sich als Einbruch (Persona-Runde 20.10.).
  // Rechter Rand der Charts (48 / 36 px) hält die letzte Beschriftung im Bild (zweizeilig, siehe AchsenTick).
  const teilMonat = new Map(monate.filter((m) => m.is_partial).map((m) => [m.month, m.until.slice(8, 10)]));
  // Zweizeilig („Okt 26“ / „(bis 29.)“): einzeilig lief die lange letzte Beschriftung bei 1.024 und 390 px in die
  // Nachbarbeschriftung („Sep 2Okt 26 (bis 29.)“, Abnahme 29.10.). Schrift wie Nivos eigene Achse (Thema + sans-serif).
  const tickSchrift = ((viz.nivo as { axis?: { ticks?: { text?: CSSProperties } } }).axis?.ticks?.text ?? {}) as CSSProperties;
  const AchsenTick = ({ x, y, textY, value }: { x: number; y: number; textY: number; value: string | number }) => {
    const m = String(value);
    const bis = teilMonat.get(m);
    return (
      <g transform={`translate(${x},${y + textY})`}>
        <text textAnchor="middle" dominantBaseline="text-before-edge" style={{ fontFamily: 'sans-serif', ...tickSchrift }}>
          {formatMonat(m)}
          {bis && (
            <tspan x={0} dy="1.2em" style={{ fontSize: 11 }}>
              (bis {bis}.)
            </tspan>
          )}
        </text>
      </g>
    );
  };
  /**
   * Linien: vier kleine Gruppen (je ≤ 30 Anfos im Monat) verknäulen sich bei null. Beim Wechsel auf Linien deshalb nur
   * die drei größten Gruppen zeigen — aber nur, wenn noch alle an sind; eine eigene Auswahl bleibt (Persona-Runde 20.10.).
   */
  const ansichtWechseln = (v: Ansicht) => {
    if (v === 'linien' && aus.size === 0 && gruppen.length > 3) {
      const summe = (k: KanalGruppeKey) => monate.reduce((s, m) => s + (m.je[k] ?? 0), 0);
      const gross = [...gruppen].sort((a, b) => summe(b.key) - summe(a.key)).slice(0, 3).map((g) => g.key);
      setAus(new Set(gruppen.map((g) => g.key).filter((k) => !gross.includes(k))));
    }
    setAnsicht(v);
  };
  const farbe = (k: string) => viz.kanalFarben[k as KanalGruppeKey] ?? viz.muted;
  const label = (k: string) => gruppen.find((g) => g.key === k)?.label ?? k;
  const tipBox = (viz.nivo.tooltip as { container: object }).container;

  // E2: die Achse muss die Vorjahreslinie fassen — Nivo rechnet „auto" nur aus den Balken bzw. Linien.
  const vjWerte = mitVorjahr ? [...vj.values()] : [];
  const stapelMax = Math.max(0, ...monate.map((m) => sichtbar.reduce((s, g) => s + zeig(m.je[g.key], m.alle), 0)));
  const vjAuswahlMax = Math.max(0, ...vjWerte.map((p) => p.auswahl ?? 0));
  const balkenMax: number | 'auto' = ansicht === 'anteil' ? 100 : vjAuswahlMax > stapelMax ? vjAuswahlMax : 'auto';
  const linienMax = Math.max(0, ...monate.flatMap((m) => sichtbar.map((g) => m.je[g.key])));
  const vjLinienMax = Math.max(0, ...vjWerte.flatMap((p) => sichtbar.map((g) => p.je[g.key] ?? 0)));
  const linieMax: number | 'auto' = vjLinienMax > linienMax ? vjLinienMax : 'auto';

  /** Gestrichelte Vorjahreslinie über den Balken: Σ der Auswahl im Monat m − 12, Lücken bleiben Lücken. */
  const VorjahrBalkenLayer = ({ xScale, yScale }: BarCustomLayerProps<BarDatum>) => {
    if (!mitVorjahr) return null;
    const xs = xScale as unknown as ((v: string) => number) & { bandwidth: () => number };
    const ys = yScale as unknown as (v: number) => number;
    const punkte = monate.map((m) => {
      const v = vj.get(m.month)?.auswahl;
      return v === null || v === undefined ? null : { x: xs(m.month) + xs.bandwidth() / 2, y: ys(v) };
    });
    return <VorjahrPfad punkte={punkte} farbe={viz.inkSecondary} grund={theme.palette.background.default} />;
  };
  /** Linienansicht: je gewählter Gruppe ihr Vorjahr, gestrichelt in der Gruppenfarbe. */
  const VorjahrLinienLayer = ({ xScale, yScale }: CustomLayerProps) => {
    if (!mitVorjahr) return null;
    const xs = xScale as unknown as (v: string) => number;
    const ys = yScale as unknown as (v: number) => number;
    return (
      <g>
        {sichtbar.map((g) => (
          <VorjahrPfad
            key={g.key}
            punkte={monate.map((m) => {
              const p = vj.get(m.month);
              return p && p.auswahl !== null ? { x: xs(m.month), y: ys(p.je[g.key] ?? 0) } : null;
            })}
            farbe={farbe(g.key)}
            grund={theme.palette.background.default}
            duenn
          />
        ))}
      </g>
    );
  };

  const umschalten = (k: KanalGruppeKey) =>
    setAus((a) => {
      const n = new Set(a);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const alleSpalten: Spalte<MonatsWerte>[] = [
    { key: 'monat', label: 'Monat', gruppe: 'Menge', pflicht: true, render: (m) => `${formatMonat(m.month)}${m.is_partial ? ` (bis ${m.until.slice(8, 10)}.${m.until.slice(5, 7)}.)` : ''}`, sortValue: (m) => m.month },
    ...gruppen.map((g) => ({
      key: g.key,
      label: g.label,
      gruppe: 'Kanalgruppen',
      align: 'right' as const,
      render: (m: MonatsWerte) => zelleBezahlt(fmt(zeig(m.je[g.key], m.alle)), m.zahler[g.key] ?? 0),
      // Nach dem ANGEZEIGTEN Wert: bei „Anteil %" der Anteil, nicht die versteckte Anzahl (Abnahme 29.10.).
      sortValue: (m: MonatsWerte) => zeig(m.je[g.key], m.alle),
    })),
    { key: 'alle', label: 'Alle', gruppe: 'Menge', pflicht: true, align: 'right', render: (m) => (ansicht === 'anteil' ? '100 %' : zelleBezahlt(fmt(m.alle), m.zahlerAlle)), sortValue: (m) => (ansicht === 'anteil' ? 100 : m.alle) },
    {
      key: 'vorjahr',
      label: TERMS.vorjahrLinie.short,
      tip: TERMS.vorjahrLinie.tip,
      einheit: ansicht === 'anteil' ? 'Anteil der Auswahl' : 'gewählte Gruppen',
      gruppe: 'Menge',
      align: 'right',
      // „—" (kein Vorjahresmonat in der Antwort, „Bisher bezahlt") sortiert als null ans Ende (Regel SortableTable).
      render: (m) => {
        const p = mitVorjahr ? vj.get(m.month) : undefined;
        if (!p || p.auswahl === null) return '—';
        return (
          <Box sx={{ lineHeight: 1.25 }}>
            {fmt(p.auswahl)}
            {p.bis && (
              <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                bis {p.bis.slice(8, 10)}.{p.bis.slice(5, 7)}.{p.bis.slice(0, 4)}
              </Box>
            )}
          </Box>
        );
      },
      sortValue: (m) => (mitVorjahr ? (vj.get(m.month)?.auswahl ?? null) : null),
    },
  ];

  // Spaltenwahl der Tabelle (Abnahme 25.10.2026: 9 Spalten liefen bei 267/414/654 px Kartenbreite waagrecht über): Monat
  // und Alle immer, die Kanalgruppen Standard nur bei Kartenbreite „breit", sonst wählbar. Eigener Speicherschlüssel.
  const tabMeta = spaltenMeta(alleSpalten);
  const tabStandards: Record<Breite, readonly string[]> = {
    breit: alleSpalten.map((s) => s.key),
    mittel: ['monat', 'alle', 'vorjahr'],
    schmal: ['monat', 'alle'],
  };
  const tabWahl = useSpaltenwahl('kanalVerlaufTabelle', tabMeta, tabStandards);
  const tabSichtbar = new Set(tabWahl.sichtbar);
  const tabSpalten = alleSpalten.filter((s) => tabSichtbar.has(s.key));

  return (
    <ChartCard
      title="Anfos je Kanal und Monat"
      howToRead={
        <>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Die letzten {spanne} Kalendermonate, der Zeitraum-Umschalter oben gilt hier nicht.
          </Box>{' '}
          Je Kanalgruppe, in denselben Farben wie „Woher die Anfos kommen“. Kanäle per Klick an- und abwählen; „Anteil %“ rechnet immer gegen alle
          Anfos des Monats, auch wenn Gruppen ausgeblendet sind. Blasse Monate sind nicht fertig: der laufende Monat
          {kennzahl === 'bezahlt'
            ? ' und alle Monate mit Anfos unter 60 Tagen — bei „bezahlt“ wächst der Betrag noch, ein niedriger Balken ist dort Jugend, kein Einbruch.'
            : ' (bis heute gezählt).'}
        </>
      }
      terms={[
        { label: TERMS.kanalGruppe.label, tip: TERMS.kanalGruppe.tip },
        { label: TERMS.wertBeiBestellung.label, tip: TERMS.wertBeiBestellung.tip },
        { label: TERMS.umsatzNetto.label, tip: TERMS.umsatzNetto.tip },
        { label: TERMS.vorjahrLinie.label, tip: TERMS.vorjahrLinie.tip },
      ]}
      source="PL-X-Bestellungen und Zahlungen, Kennung im Bestell-Link"
      freshnessKey={['plx_orders', 'anfo_werte']}
    >
      {!data ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          {data.vergleich && <Vergleichszeile v={data.vergleich} basis={basis} schwellen={data.grau_schwellen} />}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
            <ToggleButtonGroup size="small" exclusive value={kennzahl} onChange={(_e, v: Kennzahl | null) => v && setKennzahl(v)} aria-label="Kennzahl">
              {(Object.keys(KENNZAHL) as Kennzahl[]).map((k) => (
                <ToggleButton key={k} value={k} sx={{ textTransform: 'none', px: 1.25 }}>
                  {KENNZAHL[k]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={ansicht} onChange={(_e, v: Ansicht | null) => v && ansichtWechseln(v)} aria-label="Ansicht">
              <ToggleButton value="stapel" sx={{ textTransform: 'none', px: 1.25 }}>
                Gestapelt
              </ToggleButton>
              <ToggleButton value="linien" sx={{ textTransform: 'none', px: 1.25 }}>
                Linien
              </ToggleButton>
              <ToggleButton value="anteil" sx={{ textTransform: 'none', px: 1.25 }}>
                Anteil %
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={spanne} onChange={(_e, v: 12 | 24 | null) => v && setSpanne(v)} aria-label="Monate">
              <ToggleButton value={12} sx={{ textTransform: 'none', px: 1.25 }}>
                12 Monate
              </ToggleButton>
              <ToggleButton value={24} sx={{ textTransform: 'none', px: 1.25 }}>
                24 Monate
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {/* Legende = Schalter: Farbfeld wie die Marke, abgewählte Gruppen hohl. */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {gruppen.map((g) => {
              const an = !aus.has(g.key);
              return (
                <Chip
                  key={g.key}
                  size="small"
                  variant="outlined"
                  onClick={() => umschalten(g.key)}
                  aria-pressed={an}
                  label={g.label}
                  icon={
                    <Box
                      component="span"
                      sx={{ width: 10, height: 10, borderRadius: '3px', ml: '8px !important', backgroundColor: an ? farbe(g.key) : 'transparent', border: `2px solid ${farbe(g.key)}` }}
                    />
                  }
                  sx={{ color: an ? 'text.primary' : 'text.disabled' }}
                />
              );
            })}
          </Box>
          {mitVorjahr ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, fontSize: 12, color: 'text.secondary' }}>
              <svg width="26" height="8" aria-hidden="true">
                <line x1="1" y1="4" x2="25" y2="4" stroke={viz.inkSecondary} strokeWidth={2} strokeDasharray={VORJAHR_STRICH} />
              </svg>
              {ansicht === 'linien'
                ? 'Vorjahr derselben Gruppe (gestrichelt, in ihrer Farbe)'
                : `Vorjahr: gewählte Gruppen zusammen${
                    ansicht === 'anteil' ? (kennzahl === 'wert' ? ', Anteil am ganzen Erwarteten Wert des Vorjahresmonats' : ', Anteil an allen Anfos des Vorjahresmonats') : ''
                  }`}
              {spanne === 24 && ' · nur für die letzten 12 Monate'}
            </Box>
          ) : kennzahl === 'bezahlt' ? (
            <Box sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}>
              Keine Vorjahreslinie bei „{TERMS.umsatzNetto.label}“: ein Vorjahresmonat hatte zwölf Monate mehr Zeit zum Zahlen.
            </Box>
          ) : (
            anteilAlle && (
              <Box sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}>
                Vorjahreslinie bei „Anteil %“ erst, wenn Gruppen abgewählt sind — mit allen Gruppen wäre sie jeden Monat 100 %.
              </Box>
            )
          )}
          <Box sx={{ height: 320 }}>
            {sichtbar.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', pt: 4, textAlign: 'center' }}>
                Keine Kanalgruppe gewählt.
              </Typography>
            ) : ansicht === 'linien' ? (
              <ResponsiveLine
                data={sichtbar.map((g) => ({
                  id: g.key,
                  data: monate.map((m) => ({ x: m.month, y: m.je[g.key], blass: m.blass })),
                }))}
                colors={(l) => farbe(String(l.id))}
                margin={{ top: 12, right: 48, bottom: 46, left: 56 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0, max: linieMax, nice: true }}
                theme={viz.nivo}
                animate={false}
                layers={['grid', 'markers', 'axes', 'areas', 'crosshair', VorjahrLinienLayer, 'lines', 'points', 'slices', 'mesh', 'legends']}
                lineWidth={2}
                pointSize={8}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'color' }}
                // Blasse (unfertige) Monate hohl; pointSymbol bekommt den Datenpunkt, pointColor nicht (Nivo 0.83).
                pointSymbol={({ size, color, datum }) => (
                  <circle r={size / 2} fill={(datum as { blass?: boolean }).blass ? theme.palette.background.default : color} stroke={color} strokeWidth={2} />
                )}
                axisLeft={{ tickSize: 0, tickPadding: 6, format: achse(einheit) }}
                axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: ticks, renderTick: AchsenTick }}
                enableSlices="x"
                sliceTooltip={({ slice }) => (
                  <Box sx={{ ...tipBox, px: 1.5, py: 1 }}>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                      {formatMonat(String(slice.points[0]?.data.x))}
                      {mitVorjahr && vjText(String(slice.points[0]?.data.x)) ? ` · Vorjahr = ${vjText(String(slice.points[0]?.data.x))}` : ''}
                    </Box>
                    {[...slice.points]
                      .sort((a, b) => Number(b.data.y) - Number(a.data.y))
                      .map((p) => {
                        const n = monate.find((m) => m.month === String(p.data.x))?.zahler[String(p.serieId)] ?? 0;
                        const vp = mitVorjahr ? vj.get(String(p.data.x)) : undefined;
                        const vorjahrZusatz = vp && vp.auswahl !== null ? ` · Vorjahr ${fmt(vp.je[String(p.serieId)] ?? 0)}` : '';
                        return (
                          <TipRow
                            key={p.id}
                            color={farbe(String(p.serieId))}
                            label={label(String(p.serieId))}
                            value={`${mitZahlern ? `${fmt(Number(p.data.y))} · ${zahlerText(n)}` : fmt(Number(p.data.y))}${vorjahrZusatz}`}
                          />
                        );
                      })}
                  </Box>
                )}
              />
            ) : (
              <ResponsiveBar
                data={chartData as unknown as BarDatum[]}
                keys={sichtbar.map((g) => g.key)}
                indexBy="month"
                groupMode="stacked"
                margin={{ top: 8, right: 36, bottom: 46, left: 56 }}
                padding={0.3}
                valueScale={{ type: 'linear', min: 0, max: balkenMax, nice: balkenMax !== 100 }}
                // D6: unfertige Monate in der Gruppenfarbe mit geringer Deckkraft + Kontur (`UNSICHER`) statt Schleier.
                colors={(b) => (b.data.blass ? unsicherFuellung(farbe(String(b.id))) : farbe(String(b.id)))}
                borderWidth={1}
                borderColor={theme.palette.background.default}
                theme={viz.nivo}
                animate={false}
                enableLabel={false}
                axisLeft={{ tickSize: 0, tickPadding: 6, format: achse(einheit) }}
                axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: ticks, renderTick: AchsenTick }}
                tooltip={({ id, indexValue, data: d }) => (
                  <Box sx={{ ...tipBox, px: 1.5, py: 1 }}>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                      {formatMonat(String(indexValue))}
                      {d.blass ? ' · nicht fertig' : ''}
                    </Box>
                    <TipRow
                      color={farbe(String(id))}
                      label={label(String(id))}
                      value={mitZahlern ? `${fmt(Number(d[String(id)]))} · ${zahlerText(Number(d[`${String(id)}__zahler`]))}` : fmt(Number(d[String(id)]))}
                    />
                    {ansicht !== 'anteil' && (
                      <Box sx={{ opacity: 0.8 }}>
                        von {fmt(Number(d.alle))} insgesamt{mitZahlern ? ` · ${zahlerText(Number(d.zahlerAlle))}` : ''}
                      </Box>
                    )}
                    {mitVorjahr && vj.get(String(indexValue))?.auswahl != null && (
                      <Box sx={{ opacity: 0.8 }}>
                        Vorjahr {vjText(String(indexValue))}: {fmt(vj.get(String(indexValue))?.auswahl ?? null)} (gewählte Gruppen)
                      </Box>
                    )}
                  </Box>
                )}
                layers={[
                  'grid',
                  'axes',
                  'bars',
                  unsicherKonturen((b) => (b.data.data.blass ? farbe(String(b.data.id)) : null)),
                  VorjahrBalkenLayer,
                ]}
              />
            )}
          </Box>
          <Box sx={{ mt: 1 }} ref={tabWahl.messRef}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Link component="button" type="button" underline="hover" onClick={() => setTabelle((t) => !t)} sx={{ fontSize: 14 }}>
                {tabelle ? 'Zahlen ausblenden' : 'Zahlen als Tabelle'}
              </Link>
              {tabelle && <SpaltenMenue spalten={tabMeta} wahl={tabWahl} />}
            </Box>
            <Collapse in={tabelle} unmountOnExit>
              <Box sx={{ mt: 1 }}>
                <SortableTable rows={[...monate].reverse()} columns={tabSpalten} rowKey={(m) => m.month} muted={(m) => m.blass} initialSort={{ key: 'monat', dir: 'desc' }} />
              </Box>
            </Collapse>
          </Box>
          <Hinweis>
            {kennzahl === 'wert' && 'Erwarteter Wert netto; Anfos ohne Wert (z. B. Artikel noch nicht geliefert) zählen 0. '}
            {kennzahl === 'bezahlt' &&
              'Bisher bezahlt, netto bis heute, mit der Zahl der Zahler darunter — ältere Monate hatten mehr Zeit zum Zahlen, und wenige Sofortzahler lassen einen jungen Monat stark aussehen. Zwischen Monaten nur mit dem erwarteten Wert vergleichen. '}
            Vorläufig ergänzte Anfos zählen bei „Anfos“ mit, bei den Euro-Werten nicht. Der Zeitraum oben gilt hier nicht — diese Karte zeigt
            immer volle Monate rückwärts ab heute. Die gestrichelte Vorjahreslinie vergleicht den laufenden Monat mit dem Vorjahresmonat bis
            zum selben Tag.
          </Hinweis>
        </>
      )}
    </ChartCard>
  );
};

export default VerlaufKarte;
